import {
  Button,
  ConfirmDialog,
  Checkbox,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Slider,
} from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { TunaPluginRenderPropertiesProps } from '@tunasong/plugin-lib'
import type { Poll, PollOption, PollResponse } from '@tunasong/schemas'
import { isDatePoll, isManyOfManyPoll, isPoll, pollTypes } from '@tunasong/schemas'
import type { FC } from 'react'
import React from 'react'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { getAnswerCounts } from './availability.js'
import type { OptionRowHandlers } from './option-row.js'
import { OptionRow } from './option-row.js'
import { SlotListEditor } from './slot-list-editor.js'
import css from './properties.module.css'

export interface PropertiesProps extends TunaPluginRenderPropertiesProps<Poll> {
  className?: string
}

/**
 * Never splice. Responses are keyed by option index, so removing element 1 of [0,1,2]
 * re-points every stored answer for element 2 onto element 1 — silently, and the symptom
 * is someone turning up on the wrong night. The option stays as a tombstone and is
 * filtered out of every render.
 *
 * And never un-set `removed`: re-adding the same option appends a NEW index, so old
 * answers cannot reattach to a question that was asked again.
 */
const tombstone = (option: PollOption): PollOption => ({ ...option, removed: true })

export const Properties: FC<PropertiesProps> = props => {
  const { className, element, onChange } = props

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } = graphHooks.useEntityChildren<PollResponse>({
    parentId: element.id,
    filter: 'pollresponse',
  })

  const handleClick = useCallback(() => {
    if (!isPoll(element)) {
      return
    }
    const { pollOptions = [] } = element
    const updated = {
      ...element,
      pollOptions: [...pollOptions, { name: '' }],
    }
    onChange(updated)
  }, [element, onChange])

  /**
   * Row handlers are created once and read the latest poll and callback through this ref. Rebuilding them per
   * render changed every row's props on every keystroke, so all rows re-rendered (GH #1969); and a handler from
   * an earlier render wrote a whole option list from a stale poll, wiping other rows (GH #1966).
   */
  const answerCounts = getAnswerCounts(allResponses)
  const latest = useRef({ element, onChange, answerCounts })
  useLayoutEffect(() => {
    latest.current = { element, onChange, answerCounts }
  })

  /** Write one option of the latest poll. The other option objects keep their identity. */
  const updateOptionAt = (idx: number, update: (option: PollOption) => PollOption) => {
    const { element: current, onChange: change } = latest.current
    if (!isPoll(current)) {
      return
    }
    change({ ...current, pollOptions: current.pollOptions.map((option, i) => (i === idx ? update(option) : option)) })
  }

  /**
   * Renaming and removing are different risks. Renaming silently changes what people agreed
   * to, so it stays blocked. Removing is visible and discards cleanly, so it is allowed —
   * behind a confirm when anyone has answered, and immediately when nobody has. The common
   * case should not pay for the rare one.
   */
  const [pendingRemoval, setPendingRemoval] = React.useState<{ idx: number; label: string } | undefined>(undefined)

  const [rowHandlers] = React.useState<OptionRowHandlers>(() => ({
    /** Spread the existing option: `{ name }` alone dropped entityRef, start, end and removed. */
    rename: (idx, name) => updateOptionAt(idx, option => ({ ...option, name })),
    /** Linking spreads the option, so text typed before linking survives as the option's label. */
    relink: (idx, entityRef, name) =>
      updateOptionAt(idx, option => ({ ...option, entityRef, ...(name === undefined ? {} : { name }) })),
    remove: (idx, label) => {
      if ((latest.current.answerCounts.get(idx) ?? 0) > 0) {
        setPendingRemoval({ idx, label })
        return
      }
      updateOptionAt(idx, tombstone)
    },
  }))

  const handleCancelRemoval = () => setPendingRemoval(undefined)
  const handleConfirmRemoval = () => {
    if (pendingRemoval) {
      updateOptionAt(pendingRemoval.idx, tombstone)
    }
    setPendingRemoval(undefined)
  }

  const handleChangeType = useCallback(
    (ev: unknown, pollType: string) => {
      const updated = { ...element, pollType: pollType as never } satisfies Poll
      onChange(updated)
    },
    [element, onChange]
  )

  const handleQuotaChange = useCallback(
    (ev: unknown, value: number | number[]) => {
      const responseQuota = typeof value === 'number' && value > 0 ? value : undefined
      const updated = { ...element, responseQuota }
      onChange(updated)
    },
    [element, onChange]
  )

  const handleRandomize = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...element, randomizeOptions: ev.target.checked }
    onChange(updated)
  }

  if (!isPoll(element) || !hasLoadedChildren) {
    return null
  }
  const { pollOptions = [], pollType } = element

  /**
   * The panel used to be replaced wholesale by "Cannot edit poll with responses". That was a
   * crude way to make the splice above unreachable; now that removal is a tombstone, only the
   * options somebody has actually answered need freezing. Adding one is always safe, because
   * appending shifts no index.
   */
  const answerCount = (idx: number) => answerCounts.get(idx) ?? 0
  const hasAnyAnswers = answerCounts.size > 0

  /**
   * Named by what the row shows, so an unlabelled linked option reads as its entity instead of an empty
   * string. Date polls never reach this dialog (they return the slot editor below), so it says "option". GH #1970
   */
  const removalCount = pendingRemoval ? answerCount(pendingRemoval.idx) : 0
  const removalLabel = pendingRemoval?.label ?? ''
  const removalText =
    removalCount === 1
      ? `1 person has already answered for ${removalLabel}. Their answers for this option will be discarded.`
      : `${removalCount} people have already answered for ${removalLabel}. Their answers for this option will be discarded.`

  /** A rehearsal poll authors slots, not text options. */
  if (isDatePoll(element)) {
    return <SlotListEditor element={element} answerCounts={answerCounts} onChange={onChange} />
  }

  return (
    <div className={[css.root, className].filter(Boolean).join(' ')}>
      <FormControl>
        <RadioGroup aria-label="options" name="options" value={pollType ?? ''} onChange={handleChangeType}>
          {pollTypes.map(value => (
            <FormControlLabel key={value} value={value} control={<Radio />} label={value} />
          ))}
        </RadioGroup>
      </FormControl>

      {isManyOfManyPoll(element) && (
        <>
          <FormLabel className={css.formLabel}>Vote quota</FormLabel>
          <div className={css.sliderPadding}>
            <Slider
              value={typeof element.responseQuota === 'number' ? element.responseQuota : 0}
              onChange={handleQuotaChange}
              min={0}
              max={element.pollOptions?.length}
            />
          </div>
        </>
      )}

      <FormLabel className={css.formLabel}>Options</FormLabel>
      <FormControlLabel
        control={<Checkbox checked={element?.randomizeOptions} onChange={handleRandomize} />}
        label="Randomize order of options in poll"
      />
      <Typography variant="caption" className={css.helpText}>
        Add options for users to vote on. You can give the option a name, or optionally link to an entity that
        represents the option (e.g., a song or audio clip). A linked option shows the entity name unless you give it a
        label.
      </Typography>

      {pollOptions.map((option, idx) =>
        option.removed ? null : (
          <OptionRow key={idx} idx={idx} option={option} answerCount={answerCount(idx)} handlers={rowHandlers} />
        )
      )}
      {hasAnyAnswers ? (
        <Typography variant="caption" color="textSecondary" className={css.helpText}>
          Options people have already answered can&apos;t be renamed — remove and re-add instead.
        </Typography>
      ) : null}

      <Button onClick={handleClick} className={css.createButton}>
        Create option
      </Button>

      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title="Remove this option?"
        text={removalText}
        okText="Remove option"
        onCancel={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  )
}

export default Properties
